import { withAppContext } from '@/config/site'
import { getToken } from '@/utils/auth'
import request from '@/utils/request'

const DEFAULT_SETTINGS = {
  navigation: {
    enabled: true,
    items: ['课程', '资源', '讨论', 'study_history', 'notifications']
  },
  self_test: { enabled: false },
  watermark: { enabled: false, text: '' },
  notification: { enabled: true },
  monitoring: { enabled: true }
}

const parseValue = (value) => {
  if (typeof value !== 'string') return value

  try {
    return JSON.parse(value)
  } catch (error) {
    return value
  }
}

const resolveEnabled = (value, fallback) => {
  const parsed = parseValue(value)
  if (parsed && typeof parsed === 'object' && !Array.isArray(parsed) && Object.prototype.hasOwnProperty.call(parsed, 'enabled')) {
    return resolveEnabled(parsed.enabled, fallback)
  }
  if (typeof parsed === 'boolean') return parsed
  if (typeof parsed === 'number') return parsed === 1
  if (parsed === undefined || parsed === null || parsed === '') return fallback

  return ['1', 'true', 'yes', 'on'].includes(String(parsed).toLowerCase())
}

const resolveItems = (value, fallback) => {
  const parsed = parseValue(value)
  const items = parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed.items : parsed
  return Array.isArray(items) ? items.map(item => String(item)) : [...fallback]
}

export const hasStudentNavigation = (settings, names = []) => {
  if (!settings || !settings.navigation) return true
  if (settings.navigation.enabled === false) return false

  const aliases = {
    course: ['课程', 'course', 'courses'],
    progress: ['进度', 'progress', 'study-history', 'study_history'],
    study_history: ['进度', 'progress', 'study-history', 'study_history'],
    resources: ['资源', 'resource', 'resources'],
    discussion: ['讨论', 'discussion'],
    notifications: ['通知', 'notification', 'notifications']
  }
  const items = settings.navigation.items || []

  return names.some(name => {
    const candidates = aliases[name] || [name]
    return candidates.some(candidate => items.includes(candidate))
  })
}

const fetchStudentSettingsByPath = async (path, courseId) => {
  const response = await request(path, {
    data: withAppContext({
      course_id: courseId
    })
  })
  const raw = response.data && typeof response.data === 'object' ? response.data : {}
  const navigation = parseValue(raw.navigation)
  const legacyNavigation = raw.nav_config
  const selfTestValue = Object.prototype.hasOwnProperty.call(raw, 'allow_self_test') ? raw.allow_self_test : raw.self_test
  const watermarkValue = Object.prototype.hasOwnProperty.call(raw, 'show_video_watermark') ? raw.show_video_watermark : raw.watermark
  const watermark = parseValue(raw.watermark)
  const notificationValue = Object.prototype.hasOwnProperty.call(raw, 'enable_notifications') ? raw.enable_notifications : raw.notification
  const monitoringValue = Object.prototype.hasOwnProperty.call(raw, 'enable_learning_monitor') ? raw.enable_learning_monitor : raw.monitoring

  return {
    navigation: {
      enabled: resolveEnabled(raw.navigation, DEFAULT_SETTINGS.navigation.enabled),
      items: resolveItems(
        navigation && navigation.items ? navigation.items : legacyNavigation,
        DEFAULT_SETTINGS.navigation.items
      )
    },
    self_test: { enabled: resolveEnabled(selfTestValue, DEFAULT_SETTINGS.self_test.enabled) },
    watermark: {
      enabled: resolveEnabled(watermarkValue, DEFAULT_SETTINGS.watermark.enabled),
      text: String((watermark || {}).text || DEFAULT_SETTINGS.watermark.text)
    },
    notification: { enabled: resolveEnabled(notificationValue, DEFAULT_SETTINGS.notification.enabled) },
    monitoring: { enabled: resolveEnabled(monitoringValue, DEFAULT_SETTINGS.monitoring.enabled) }
  }
}

export const fetchStudentSettings = async (courseId = 0) => {
  const normalizedCourseId = Number(courseId) || 0
  const endpoints = getToken()
    ? ['/page/course/student-settings/index', '/page/course/settings']
    : ['/page/course/settings']

  let lastError = null

  for (const path of endpoints) {
    try {
      return await fetchStudentSettingsByPath(path, normalizedCourseId)
    } catch (error) {
      lastError = error
    }
  }

  throw lastError || new Error('学生端设置加载失败')
}
